import { isNil } from "lodash-es";

import type { GameTypeEnum } from "@/common/composables/use-vocab-quiz";
import config from "@/common/config";
import { ClassQuizStateEnum } from "@/common/types/vocab";

import ApiService from "./api.service";

type GetQuizQuestionsOptions = {
  limit?: number;
  answerFilter?: "correct" | "incorrect";
};

export default {
  NAMESPACE: config.api.vocab.namespace,
  VERSION: config.api.vocab.versions.v2,

  getLists(language: string) {
    const params = new URLSearchParams({ language });
    return ApiService.get(`/lists?${params}`, this.VERSION, this.NAMESPACE);
  },

  getList(shareCode: string) {
    return ApiService.get(`/lists/${encodeURIComponent(shareCode)}`, this.VERSION, this.NAMESPACE);
  },

  createList(name: string, language: string) {
    return ApiService.post("lists", { name, language }, this.VERSION, this.NAMESPACE);
  },

  addList(shareCode: string) {
    return ApiService.put(
      `lists/${encodeURIComponent(shareCode)}`,
      {},
      this.VERSION,
      this.NAMESPACE
    );
  },

  editList(shareCode: string, name: string) {
    return ApiService.patch(
      `lists/${encodeURIComponent(shareCode)}`,
      { name },
      this.VERSION,
      this.NAMESPACE
    );
  },

  deleteList(shareCode: string) {
    return ApiService.delete(
      `lists/${encodeURIComponent(shareCode)}`,
      this.VERSION,
      this.NAMESPACE
    );
  },

  openWord(shareCode: string, wordId: number) {
    return ApiService.patch(
      `lists/${encodeURIComponent(shareCode)}/words/${wordId}`,
      { openedAt: new Date().toISOString() },
      this.VERSION,
      this.NAMESPACE
    );
  },

  deleteWord(shareCode: string, wordId: number) {
    return ApiService.delete(
      `lists/${encodeURIComponent(shareCode)}/words/${wordId}`,
      this.VERSION,
      this.NAMESPACE
    );
  },

  createQuiz(shareCode: string, gameType: GameTypeEnum) {
    return ApiService.post(`/quizzes`, { shareCode, gameType }, this.VERSION, this.NAMESPACE);
  },

  createQuizAnswer(quizCode: string, questionId: number, answer?: string) {
    return ApiService.post(
      `/quizzes/${quizCode}/answers`,
      { questionId, answer },
      this.VERSION,
      this.NAMESPACE
    );
  },

  skipQuizRound(quizCode: string, roundId: number) {
    return ApiService.patch(
      `/quizzes/${quizCode}/rounds/${roundId}`,
      { isSkipped: true },
      this.VERSION,
      this.NAMESPACE
    );
  },

  getQuizRounds(quizCode: string) {
    return ApiService.get(`/quizzes/${quizCode}/rounds`, this.VERSION, this.NAMESPACE);
  },

  getQuizQuestions(quizCode: string, options: Partial<GetQuizQuestionsOptions> = {}) {
    const params = new URLSearchParams();

    // UrlSearchParams includes null/undefined values, so this is the simplest way to filter them out.
    Object.entries(options).forEach(
      ([key, value]) => !isNil(value) && params.append(key, value.toString())
    );

    return ApiService.get(`/quizzes/${quizCode}/questions?${params}`, this.VERSION, this.NAMESPACE);
  },

  search(query: string, language: string) {
    return ApiService.post(`/search`, { query, language }, this.VERSION, this.NAMESPACE);
  },

  addWord(shareCode: string, lemmaId: number, translationId: number) {
    return ApiService.post(
      `/lists/${shareCode}/words`,
      { lemmaId, translationId },
      this.VERSION,
      this.NAMESPACE
    );
  },

  createClassQuiz(shareCode: string) {
    return ApiService.post(
      `/lists/${shareCode}/class-quizzes`,
      { shareCode },
      this.VERSION,
      this.NAMESPACE
    );
  },

  getClassQuiz(quizCode: string) {
    return ApiService.get(`/class-quizzes/${quizCode}`, this.VERSION, this.NAMESPACE);
  },

  classQuizSetPromptingState(quizCode: string) {
    return ApiService.patch(
      `/class-quizzes/${quizCode}`,
      { state: ClassQuizStateEnum.Prompting },
      this.VERSION,
      this.NAMESPACE
    );
  },

  classQuizSetRankingState(quizCode: string) {
    return ApiService.patch(
      `/class-quizzes/${quizCode}`,
      { state: ClassQuizStateEnum.Ranking },
      this.VERSION,
      this.NAMESPACE
    );
  },

  endClassQuiz(quizCode: string) {
    return ApiService.patch(
      `/class-quizzes/${quizCode}`,
      { state: ClassQuizStateEnum.Ended },
      this.VERSION,
      this.NAMESPACE
    );
  },

  getClassQuizStats(quizCode: string) {
    return ApiService.get(`/class-quizzes/${quizCode}/stats`, this.VERSION, this.NAMESPACE);
  },

  createClassQuizAnswer(quizCode: string, questionId: number, answer: string) {
    return ApiService.post(
      `/class-quizzes/${quizCode}/answers`,
      { answer, questionId },
      this.VERSION,
      this.NAMESPACE
    );
  },

  getAchievements() {
    return ApiService.get(`/achievements`, this.VERSION, this.NAMESPACE);
  },

  getLatestAchievements() {
    // We fetch a bit more achievements than actually needed. This is to make sure the filtering,
    // doesn't remove import results (if they were sorted last)
    return ApiService.get(
      `/achievements?orderBy=updatedAt&order=desc&limit=5`,
      this.VERSION,
      this.NAMESPACE
    );
  },

  openBadge(badgeName: string) {
    return ApiService.patch(
      `/badges/${badgeName}`,
      { openedAt: new Date().toISOString() },
      this.VERSION,
      this.NAMESPACE
    );
  }
};
